import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from './contexts/AuthContext'
import { useMembership } from './hooks/useMembership'
import { useNotification } from './contexts/NotificationContext'
import { supabase } from './lib/supabase'
import { currencies } from './components/CurrencySelector'
import { convertPnl } from './hooks/useDashboardCurrency'
import Footer from './components/Footer'
import AccountsUpgradeCard from './components/AccountsUpgradeCard'
import AccountFeeRulesEditor from './components/AccountFeeRulesEditor'
import { buildFeeStateFromRules, emptyFeeRuleFormMap } from './lib/accountFeeRules'
import { fetchAccountFeeConfig, saveAccountFeeRules } from './lib/accountFeeRulesDb'
import type { AccountFeeRuleForm } from './lib/accountFeeRules'

interface Account {
  id: string
  user_id: string
  name: string
  description: string | null
  currency: string
  platform: string
  initial_balance: number | null
  created_at: string
  updated_at: string
}

type LedgerType = 'deposit' | 'withdrawal' | 'buy' | 'sell' | 'fee' | 'adjustment'
type LedgerSource = 'opening' | 'trade' | 'manual'

interface AccountTransaction {
  id: string
  account_id: string
  type: LedgerType
  amount: number
  original_amount: number | null
  original_currency: string | null
  fx_rate: number | null
  trade_id: string | null
  occurred_at: string
  note: string | null
  source: LedgerSource
  created_at: string
}

interface LedgerRow extends AccountTransaction {
  runningBalance: number
}

function formatAmount(value: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatFxRate(rate: number) {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(rate)
}

function toDateTimeLocal(value: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(value.getHours())}:${pad(value.getMinutes())}`
}

function buildLedgerRows(rows: AccountTransaction[]): LedgerRow[] {
  const ordered = [...rows].sort((a, b) => {
    const byOccurred = a.occurred_at.localeCompare(b.occurred_at)
    if (byOccurred !== 0) return byOccurred
    const byCreated = (a.created_at || '').localeCompare(b.created_at || '')
    if (byCreated !== 0) return byCreated
    return a.id.localeCompare(b.id)
  })
  let running = 0
  return ordered.map((row) => {
    running += Number(row.amount) || 0
    return { ...row, runningBalance: running }
  })
}

function Accounts({ onBack, setCurrentPage, embedded }: {
  onBack: () => void
  setCurrentPage: (page: string) => void
  embedded?: boolean
}) {
  void setCurrentPage
  const { t } = useTranslation(['common', 'accounts'])
  const { user } = useAuth()
  const { canUseAccounts, maxAccounts, limitsLoading } = useMembership()
  const { showError, showConfirm, showSuccess } = useNotification()
  const navigate = useNavigate()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountPnl, setAccountPnl] = useState<Record<string, number>>({})
  const [accountBalances, setAccountBalances] = useState<Record<string, number>>({})
  const [transactions, setTransactions] = useState<AccountTransaction[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState<Account | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formCurrency, setFormCurrency] = useState('USD')
  const [formPlatform, setFormPlatform] = useState('')
  const [formInitialBalance, setFormInitialBalance] = useState('')
  const [saveLoading, setSaveLoading] = useState(false)
  const [ledgerAccount, setLedgerAccount] = useState<Account | null>(null)
  const [showCashModal, setShowCashModal] = useState(false)
  const [cashType, setCashType] = useState<'deposit' | 'withdrawal'>('deposit')
  const [cashAmount, setCashAmount] = useState('')
  const [cashDate, setCashDate] = useState('')
  const [cashNote, setCashNote] = useState('')
  const [cashLoading, setCashLoading] = useState(false)
  const [deletingTxId, setDeletingTxId] = useState<string | null>(null)
  const [feeEnabledScopes, setFeeEnabledScopes] = useState<string[]>([])
  const [formFeeForms, setFormFeeForms] = useState<Record<string, AccountFeeRuleForm>>(() => emptyFeeRuleFormMap())

  const typeLabel = (type: LedgerType) => t(`accounts:txType.${type}`)

  const resetFeeRuleForms = () => {
    setFeeEnabledScopes([])
    setFormFeeForms(emptyFeeRuleFormMap())
  }

  const fetchAccounts = async () => {
    if (!user) return
    try {
      setLoading(true)
      const [accountsResult, tradesResult, txResult] = await Promise.all([
        supabase
          .from('accounts')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('trades_with_market_data')
          .select('account_id, pnl, currency, exit_date')
          .eq('user_id', user.id)
          .not('account_id', 'is', null)
          .not('pnl', 'is', null),
        supabase
          .from('account_transactions')
          .select('id, account_id, type, amount, original_amount, original_currency, fx_rate, trade_id, occurred_at, note, source, created_at')
          .eq('user_id', user.id)
          .order('occurred_at', { ascending: true }),
      ])
      if (accountsResult.error) throw accountsResult.error
      const fetchedAccounts = (accountsResult.data as Account[]) || []
      setAccounts(fetchedAccounts)

      if (txResult.error && txResult.error.code !== 'PGRST205' && txResult.error.code !== '42P01') {
        throw txResult.error
      }
      const fetchedTx = (!txResult.error ? txResult.data as AccountTransaction[] : []) || []
      setTransactions(fetchedTx)

      const balanceMap: Record<string, number> = {}
      for (const row of fetchedTx) {
        balanceMap[row.account_id] = (balanceMap[row.account_id] ?? 0) + Number(row.amount)
      }
      setAccountBalances(balanceMap)

      const pnlMap: Record<string, number> = {}

      if (!tradesResult.error && tradesResult.data && tradesResult.data.length > 0) {
        const trades = tradesResult.data as {
          account_id: string
          pnl: number
          currency: string
          exit_date: string | null
        }[]

        const accountCurrencyMap: Record<string, string> = {}
        for (const acc of fetchedAccounts) {
          accountCurrencyMap[acc.id] = acc.currency
        }

        const conversionPairs = new Map<string, { date: string; tradeCurrency: string; accountCurrency: string }>()
        for (const trade of trades) {
          const accCurrency = accountCurrencyMap[trade.account_id]
          if (!accCurrency || !trade.currency || trade.currency === accCurrency || !trade.exit_date) continue
          const date = trade.exit_date.split('T')[0]
          const key = `${date}:${accCurrency}:${trade.currency}`
          if (!conversionPairs.has(key)) {
            conversionPairs.set(key, { date, tradeCurrency: trade.currency, accountCurrency: accCurrency })
          }
        }

        const ratesMap = new Map<string, number>()
        for (const { date, tradeCurrency, accountCurrency } of conversionPairs.values()) {
          let { data: rateData } = await supabase
            .from('exchange_rates')
            .select('rate')
            .eq('base_currency', accountCurrency)
            .eq('target_currency', tradeCurrency)
            .eq('rate_date', date)
            .limit(1)
            .maybeSingle()

          if (!rateData) {
            const { data: nearest } = await supabase
              .from('exchange_rates')
              .select('rate')
              .eq('base_currency', accountCurrency)
              .eq('target_currency', tradeCurrency)
              .lte('rate_date', date)
              .order('rate_date', { ascending: false })
              .limit(1)
              .maybeSingle()
            rateData = nearest ?? null
          }

          if (rateData) {
            ratesMap.set(`${date}:${accountCurrency}:${tradeCurrency}`, parseFloat(rateData.rate.toString()))
          } else {
            const { data: reverse } = await supabase
              .from('exchange_rates')
              .select('rate')
              .eq('base_currency', tradeCurrency)
              .eq('target_currency', accountCurrency)
              .lte('rate_date', date)
              .order('rate_date', { ascending: false })
              .limit(1)
              .maybeSingle()
            if (reverse) {
              ratesMap.set(`${date}:${tradeCurrency}:${accountCurrency}`, parseFloat(reverse.rate.toString()))
            }
          }
        }

        for (const trade of trades) {
          const accCurrency = accountCurrencyMap[trade.account_id]
          if (!accCurrency) continue
          const converted = convertPnl(
            { pnl: trade.pnl, exit_date: trade.exit_date, currency: trade.currency },
            accCurrency,
            ratesMap
          )
          pnlMap[trade.account_id] = (pnlMap[trade.account_id] ?? 0) + converted
        }
      }

      setAccountPnl(pnlMap)
      setLedgerAccount((current) => current ? fetchedAccounts.find((a) => a.id === current.id) ?? current : null)
    } catch (e: any) {
      showError(e?.message || 'Failed to load accounts')
      setAccounts([])
      setTransactions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAccounts()
  }, [user])

  const openCreate = () => {
    setEditingAccount(null)
    setFormName('')
    setFormDescription('')
    setFormCurrency('USD')
    setFormPlatform('')
    setFormInitialBalance('')
    resetFeeRuleForms()
    setShowModal(true)
  }

  const openEdit = (account: Account) => {
    setEditingAccount(account)
    setFormName(account.name || '')
    setFormDescription(account.description || '')
    setFormCurrency(account.currency || 'USD')
    setFormPlatform(account.platform || '')
    setFormInitialBalance(account.initial_balance != null ? String(account.initial_balance) : '')
    resetFeeRuleForms()
    setShowModal(true)
  }

  useEffect(() => {
    if (!showModal || !editingAccount || !user) return
    let cancelled = false
    fetchAccountFeeConfig(supabase, editingAccount.id)
      .then((config) => {
        if (cancelled) return
        const feeState = buildFeeStateFromRules(config.rules)
        setFeeEnabledScopes(feeState.enabledScopes)
        setFormFeeForms(feeState.forms)
      })
      .catch(() => {
        if (!cancelled) resetFeeRuleForms()
      })
    return () => { cancelled = true }
  }, [showModal, editingAccount?.id, user?.id])

  const openCash = (account: Account, type: 'deposit' | 'withdrawal') => {
    setLedgerAccount(account)
    setCashType(type)
    setCashAmount('')
    setCashNote('')
    setCashDate(toDateTimeLocal(new Date()))
    setShowCashModal(true)
  }

  const handleSave = async () => {
    if (!user) return
    const name = formName.trim()
    if (!name) {
      showError(t('accounts:nameRequired'))
      return
    }
    const platform = formPlatform.trim()
    if (!platform) {
      showError(t('accounts:platformRequired'))
      return
    }
    if (!editingAccount && maxAccounts !== null && accounts.length >= maxAccounts) {
      showError(t('accounts:limitReached'))
      return
    }

    let parsedInitialBalance: number | null = null
    if (formInitialBalance.trim() !== '') {
      const val = parseFloat(formInitialBalance.replace(',', '.'))
      if (isNaN(val)) {
        showError(t('accounts:initialBalanceInvalid'))
        return
      }
      parsedInitialBalance = val
    }

    setSaveLoading(true)
    try {
      let accountId = editingAccount?.id
      if (editingAccount) {
        const { error } = await supabase
          .from('accounts')
          .update({
            name,
            description: formDescription.trim() || null,
            currency: formCurrency,
            platform,
            initial_balance: parsedInitialBalance,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingAccount.id)
          .eq('user_id', user.id)
        if (error) throw error
      } else {
        const { data: created, error } = await supabase
          .from('accounts')
          .insert({
            user_id: user.id,
            name,
            description: formDescription.trim() || null,
            currency: formCurrency,
            platform,
            initial_balance: parsedInitialBalance,
          })
          .select('id')
          .single()
        if (error) throw error
        accountId = created.id
      }
      if (accountId) {
        await saveAccountFeeRules(supabase, accountId, feeEnabledScopes, formFeeForms)
      }
      setShowModal(false)
      fetchAccounts()
    } catch (e: any) {
      showError(e?.message || 'Failed to save account')
    } finally {
      setSaveLoading(false)
    }
  }

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const confirmed = await showConfirm({
      title: t('accounts:deleteTitle'),
      message: t('accounts:deleteConfirm'),
      confirmText: t('buttons.delete', { ns: 'common' }),
      cancelText: t('buttons.cancel', { ns: 'common' }),
      confirmButtonStyle: 'danger',
    })
    if (!confirmed) return
    try {
      setDeletingId(id)
      const { error } = await supabase
        .from('accounts')
        .delete()
        .eq('id', id)
        .eq('user_id', user!.id)
      if (error) throw error
      if (ledgerAccount?.id === id) setLedgerAccount(null)
      fetchAccounts()
    } catch (e: any) {
      showError(e?.message || 'Failed to delete account')
    } finally {
      setDeletingId(null)
    }
  }

  const handleSaveCash = async () => {
    if (!user || !ledgerAccount) return
    const val = parseFloat(cashAmount.replace(',', '.'))
    if (isNaN(val) || val <= 0) {
      showError(t('accounts:cashAmountInvalid'))
      return
    }
    const occurredAt = cashDate ? new Date(cashDate) : new Date()
    if (isNaN(occurredAt.getTime())) {
      showError(t('accounts:cashDateInvalid'))
      return
    }

    setCashLoading(true)
    try {
      const { error } = await supabase.from('account_transactions').insert({
        account_id: ledgerAccount.id,
        user_id: user.id,
        type: cashType,
        amount: cashType === 'deposit' ? val : -val,
        original_amount: val,
        original_currency: ledgerAccount.currency,
        fx_rate: null,
        occurred_at: occurredAt.toISOString(),
        note: cashNote.trim() || null,
        source: 'manual',
      })
      if (error) throw error
      setShowCashModal(false)
      showSuccess(t('accounts:cashSaved'))
      fetchAccounts()
    } catch (e: any) {
      showError(e?.message || 'Failed to save transaction')
    } finally {
      setCashLoading(false)
    }
  }

  const handleDeleteTx = async (tx: AccountTransaction) => {
    if (tx.source !== 'manual') return
    const confirmed = await showConfirm({
      title: t('accounts:deleteTransaction'),
      message: t('accounts:deleteTransactionConfirm'),
      confirmText: t('buttons.delete', { ns: 'common' }),
      cancelText: t('buttons.cancel', { ns: 'common' }),
      confirmButtonStyle: 'danger',
    })
    if (!confirmed) return
    try {
      setDeletingTxId(tx.id)
      const { error } = await supabase
        .from('account_transactions')
        .delete()
        .eq('id', tx.id)
        .eq('user_id', user!.id)
        .eq('source', 'manual')
      if (error) throw error
      fetchAccounts()
    } catch (e: any) {
      showError(e?.message || 'Failed to delete transaction')
    } finally {
      setDeletingTxId(null)
    }
  }

  const W = embedded ? 'div' : 'main'
  const contentClass = embedded ? 'dashboard-page-content' : 'max-w-6xl mx-auto px-4 md:px-6 py-6'

  const canAddAccount = !limitsLoading && canUseAccounts && (maxAccounts === null || accounts.length < maxAccounts)
  const ledgerRows = ledgerAccount ? buildLedgerRows(transactions.filter((row) => row.account_id === ledgerAccount.id)) : []

  return (
    <>
      <W className={contentClass}>
        {!embedded && (
          <section className="hidden md:flex items-center justify-center gap-4 mb-6">
            <button
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors flex items-center justify-center"
              title={t('buttons.back', { ns: 'common' })}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/app/all-trades')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              {t('nav.allTrades', { ns: 'common' })}
            </button>
            <button
              onClick={() => navigate('/app/analytics')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              {t('nav.analytics', { ns: 'common' })}
            </button>
            <button
              onClick={() => navigate('/app/tools')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              {t('nav.tools', { ns: 'common' })}
            </button>
            <button
              onClick={() => navigate('/app/strategies')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              {t('nav.strategies', { ns: 'common' })}
            </button>
            <button
              onClick={() => navigate('/app/accounts')}
              className="px-6 py-2 text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
            >
              {t('nav.accounts', { ns: 'common' })}
            </button>
            <button
              onClick={() => navigate('/app/community')}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-app-box border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-orange-500 hover:text-orange-500 transition-colors"
            >
              {t('nav.community', { ns: 'common' })}
            </button>
          </section>
        )}

        <div className="bg-app-box shadow-sm border border-gray-200 rounded-2xl p-4 md:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{t('accounts:title')}</h2>
              <p className="text-xs md:text-sm text-gray-500 mt-1">
                {t('accounts:subtitle')}
                {!limitsLoading && maxAccounts !== null && canUseAccounts && (
                  <span className="ml-2 text-gray-600">
                    ({t('accounts:limitCount', { current: accounts.length, max: maxAccounts })})
                  </span>
                )}
              </p>
            </div>
            {canUseAccounts && (
              <button
                onClick={openCreate}
                disabled={!canAddAccount}
                className="w-full sm:w-auto px-6 py-2.5 text-sm font-bold text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-orange-500"
              >
                + {t('accounts:addAccount')}
              </button>
            )}
          </div>

          {!limitsLoading && !canUseAccounts && (
            <div className="mb-6">
              <AccountsUpgradeCard variant="feature-unavailable" />
            </div>
          )}

          {!limitsLoading && canUseAccounts && maxAccounts !== null && accounts.length >= maxAccounts && accounts.length > 0 && (
            <div className="mb-6">
              <AccountsUpgradeCard variant="limit-reached" maxAccounts={maxAccounts} />
            </div>
          )}

          {loading && canUseAccounts ? (
            <div className="py-12 text-center text-sm text-gray-500">{t('header.loading', { ns: 'common' })}</div>
          ) : !canUseAccounts ? null : accounts.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-sm text-gray-500 mb-4">{t('accounts:noAccounts')}</p>
              <button
                onClick={openCreate}
                disabled={!canAddAccount}
                className="px-6 py-2.5 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('accounts:addAccount')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accounts.map((account) => {
                const totalPnl = accountPnl[account.id] ?? 0
                const hasLedger = accountBalances[account.id] != null
                const currentBalance = hasLedger ? accountBalances[account.id] : null
                const hasTrades = accountPnl[account.id] != null
                const hasBalance = account.initial_balance != null

                return (
                  <div
                    key={account.id}
                    className="bg-white border border-gray-200 rounded-xl p-4 hover:border-gray-300 hover:shadow-md transition-all"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <button
                        type="button"
                        onClick={() => setLedgerAccount(account)}
                        className="text-left"
                      >
                        <h3 className="text-base font-semibold text-gray-900">{account.name}</h3>
                      </button>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openEdit(account)}
                          className="p-1.5 text-orange-600 hover:bg-orange-100 rounded-full transition-colors"
                          title={t('buttons.edit', { ns: 'common' })}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={(e) => handleDelete(account.id, e)}
                          disabled={deletingId === account.id}
                          className="p-1.5 text-red-600 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                          title={t('buttons.delete', { ns: 'common' })}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h14" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {account.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{account.description}</p>
                    )}

                    <div className="flex flex-wrap gap-2 text-xs mb-3">
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">{account.currency}</span>
                      <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">{account.platform}</span>
                    </div>

                    {(hasBalance || hasTrades || hasLedger) && (
                      <div className="border-t border-gray-100 pt-3 space-y-1.5">
                        {hasBalance && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">{t('accounts:initialBalance')}</span>
                            <span className="font-medium text-gray-700">
                              {formatAmount(account.initial_balance!, account.currency)}
                            </span>
                          </div>
                        )}
                        {hasTrades && (
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">{t('accounts:totalPnl')}</span>
                            <span className={`font-medium ${totalPnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {totalPnl >= 0 ? '+' : ''}{formatAmount(totalPnl, account.currency)}
                            </span>
                          </div>
                        )}
                        {currentBalance != null && (
                          <div className="flex justify-between items-center text-xs pt-1 border-t border-gray-100">
                            <span className="text-gray-600 font-medium">{t('accounts:currentBalance')}</span>
                            <span className={`font-bold text-sm ${currentBalance >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                              {formatAmount(currentBalance, account.currency)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => setLedgerAccount(account)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        {t('accounts:viewLedger')}
                      </button>
                      <button
                        type="button"
                        onClick={() => openCash(account, 'deposit')}
                        className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100"
                      >
                        {t('accounts:deposit')}
                      </button>
                      <button
                        type="button"
                        onClick={() => openCash(account, 'withdrawal')}
                        className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100"
                      >
                        {t('accounts:withdrawal')}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </W>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div
            className="bg-app-box rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingAccount ? t('accounts:editAccount') : t('accounts:addAccount')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('accounts:name')}</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={t('accounts:namePlaceholder')}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('accounts:description')}</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder={t('accounts:descriptionPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('accounts:currency')}</label>
                <select
                  value={formCurrency}
                  onChange={(e) => setFormCurrency(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                >
                  {currencies.map((c) => (
                    <option key={c.code} value={c.code}>{c.code} - {c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('accounts:platform')}</label>
                <input
                  type="text"
                  value={formPlatform}
                  onChange={(e) => setFormPlatform(e.target.value)}
                  placeholder={t('accounts:platformPlaceholder')}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('accounts:initialBalance')}
                  <span className="ml-1 text-xs font-normal text-gray-400">({t('accounts:optional', { ns: 'accounts' })})</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formInitialBalance}
                    onChange={(e) => setFormInitialBalance(e.target.value)}
                    placeholder={t('accounts:initialBalancePlaceholder')}
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">
                    {formCurrency}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-400">{t('accounts:initialBalanceHint')}</p>
              </div>
              <AccountFeeRulesEditor
                enabledScopes={feeEnabledScopes}
                onEnabledScopesChange={setFeeEnabledScopes}
                forms={formFeeForms}
                onFormsChange={setFormFeeForms}
                currency={formCurrency}
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {saveLoading ? t('header.loading', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {ledgerAccount && !showCashModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setLedgerAccount(null)}>
          <div
            className="bg-app-box rounded-2xl p-6 max-w-3xl w-full shadow-xl border border-gray-200 max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{ledgerAccount.name}</h3>
                <p className="text-sm text-gray-500">{t('accounts:ledgerTitle')}</p>
              </div>
              <button
                type="button"
                onClick={() => setLedgerAccount(null)}
                className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="text-sm">
                <span className="text-gray-500">{t('accounts:currentBalance')}</span>
                <span className="ml-2 font-bold text-gray-900">
                  {formatAmount(accountBalances[ledgerAccount.id] ?? 0, ledgerAccount.currency)}
                </span>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => openCash(ledgerAccount, 'deposit')}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  {t('accounts:deposit')}
                </button>
                <button
                  type="button"
                  onClick={() => openCash(ledgerAccount, 'withdrawal')}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                >
                  {t('accounts:withdrawal')}
                </button>
              </div>
            </div>

            <div className="overflow-auto border border-gray-200 rounded-xl">
              {ledgerRows.length === 0 ? (
                <p className="p-6 text-sm text-center text-gray-500">{t('accounts:noTransactions')}</p>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 text-gray-500">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">{t('accounts:occurredAt')}</th>
                      <th className="text-left font-medium px-3 py-2">{t('accounts:txTypeLabel')}</th>
                      <th className="text-left font-medium px-3 py-2">{t('accounts:note')}</th>
                      <th className="text-right font-medium px-3 py-2">{t('accounts:originalAmount')}</th>
                      <th className="text-right font-medium px-3 py-2">{t('accounts:fxRate')}</th>
                      <th className="text-right font-medium px-3 py-2">{t('accounts:ledgerAmount')}</th>
                      <th className="text-right font-medium px-3 py-2">{t('accounts:runningBalance')}</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerRows.map((row) => {
                      const originalCurrency = row.original_currency || ledgerAccount.currency
                      const showFx = row.fx_rate != null && originalCurrency !== ledgerAccount.currency
                      return (
                        <tr key={row.id} className="border-t border-gray-100">
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {new Date(row.occurred_at).toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700">{typeLabel(row.type)}</span>
                          </td>
                          <td className="px-3 py-2 text-gray-500 max-w-[160px] truncate">{row.note || '—'}</td>
                          <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                            {row.original_amount != null
                              ? formatAmount(Number(row.original_amount), originalCurrency)
                              : '—'}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                            {showFx ? formatFxRate(Number(row.fx_rate)) : '—'}
                          </td>
                          <td className={`px-3 py-2 text-right font-medium whitespace-nowrap ${Number(row.amount) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                            {Number(row.amount) >= 0 ? '+' : ''}{formatAmount(Number(row.amount), ledgerAccount.currency)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-900 whitespace-nowrap">
                            {formatAmount(row.runningBalance, ledgerAccount.currency)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {row.source === 'manual' && (
                              <button
                                type="button"
                                onClick={() => handleDeleteTx(row)}
                                disabled={deletingTxId === row.id}
                                className="text-red-600 hover:underline disabled:opacity-50"
                              >
                                {t('buttons.delete', { ns: 'common' })}
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {showCashModal && ledgerAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setShowCashModal(false)}>
          <div
            className="bg-app-box rounded-2xl p-6 max-w-md w-full shadow-xl border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {cashType === 'deposit' ? t('accounts:deposit') : t('accounts:withdrawal')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('accounts:cashAmount')}</label>
                <div className="relative">
                  <input
                    type="number"
                    value={cashAmount}
                    onChange={(e) => setCashAmount(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm pr-16"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium pointer-events-none">
                    {ledgerAccount.currency}
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('accounts:cashDate')}</label>
                <input
                  type="datetime-local"
                  value={cashDate}
                  onChange={(e) => setCashDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('accounts:cashNote')}</label>
                <input
                  type="text"
                  value={cashNote}
                  onChange={(e) => setCashNote(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={() => setShowCashModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                {t('buttons.cancel', { ns: 'common' })}
              </button>
              <button
                type="button"
                onClick={handleSaveCash}
                disabled={cashLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-orange-500 rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {cashLoading ? t('header.loading', { ns: 'common' }) : t('buttons.save', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {!embedded && (
        <Footer
          currentPage="accounts"
          onNavigate={(page) => {
            if (page === 'home') navigate('/app')
            else if (page === 'privacy-policy') navigate('/privacy')
            else if (page === 'terms-and-conditions') navigate('/terms')
            else if (page === 'risk-warning') navigate('/risk-warning')
            else if (page === 'documentation') navigate('/documentation')
            else if (page === 'contact') navigate('/contact')
            else if (page === 'pricing') window.location.href = '/pricing'
            else if (page === 'changelog') navigate('/changelog')
          }}
        />
      )}
    </>
  )
}

export default Accounts
